import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  addDoc,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../firebase';
import { User, UserRole, UserStatus } from '../types';

export class AdminService {
  private static readonly USERS_COLLECTION = 'users';
  private static readonly ADMIN_CONFIG_COLLECTION = 'admin_config';
  private static readonly FIRST_ADMIN_DOC = 'first_admin';

  /**
   * Check if there's already a first admin in the system
   */
  static async isFirstAdminExists(): Promise<boolean> {
    try {
      // Check if there are any admin users in the system
      const usersSnapshot = await getDocs(collection(db, this.USERS_COLLECTION));
      const adminUsers = usersSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.role === UserRole.ADMIN;
      });
      
      console.log('🔍 Found admin users:', adminUsers.length);
      return adminUsers.length > 0;
    } catch (error) {
      console.error('Error checking first admin:', error);
      return false;
    }
  }

  /**
   * Create the first admin user (special privilege)
   */
  static async createFirstAdmin(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone: string;
  }): Promise<string> {
    try {
      console.log('🔍 Starting first admin creation process...');
      console.log('📝 User data:', userData);
      
      // Check if first admin already exists
      console.log('🔍 Checking if first admin exists...');
      const firstAdminExists = await AdminService.isFirstAdminExists();
      console.log('📊 First admin exists:', firstAdminExists);
      
      if (firstAdminExists) {
        throw new Error('First admin already exists in the system');
      }

      console.log('✅ First admin check passed, proceeding with creation...');
      
      console.log('🔐 Creating Firebase Auth user...');
      // Create the Firebase Auth user first
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase Auth user created with UID:', firebaseUser.uid);
      
      console.log('📝 Creating Firestore user document...');
      // Create the Firestore user document with the Firebase UID
      await setDoc(doc(db, AdminService.USERS_COLLECTION, firebaseUser.uid), {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        role: UserRole.ADMIN,
        createdAt: new Date(),
        isActive: true,
        status: UserStatus.ACTIVE,
      });
      
      console.log('✅ Firestore user document created successfully');

      console.log('📝 Creating admin config document...');
      // Mark this user as the first admin
      await setDoc(doc(db, AdminService.ADMIN_CONFIG_COLLECTION, AdminService.FIRST_ADMIN_DOC), {
        adminId: firebaseUser.uid,
        userEmail: userData.email,
        createdAt: new Date(),
        isFirstAdmin: true,
      });
      
      console.log('✅ Admin config document created successfully');

      return 'First admin created successfully';
    } catch (error) {
      console.error('❌ Error creating first admin:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : String(error),
        code: (error as any)?.code || 'unknown',
        stack: error instanceof Error ? error.stack : 'unknown'
      });
      throw error;
    }
  }

  /**
   * Create a new admin user (only first admin can do this)
   */
  static async createAdmin(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone: string;
  }): Promise<string> {
    try {
      console.log('🔍 Starting admin creation process...');
      
      // Get the current authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Vous devez être connecté pour créer un admin');
      }
      
      // Check if the current user is the first admin
      const isCurrentUserFirstAdmin = await AdminService.isUserFirstAdmin(currentUser.uid);
      if (!isCurrentUserFirstAdmin) {
        throw new Error('Seul le premier administrateur peut créer de nouveaux admins');
      }

      console.log('✅ Current user is first admin, proceeding with admin creation...');

      // Create the Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase Auth user created with UID:', firebaseUser.uid);

      // Create the Firestore user document using the Firebase Auth UID
      await setDoc(doc(db, AdminService.USERS_COLLECTION, firebaseUser.uid), {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        role: UserRole.ADMIN,
        createdAt: new Date(),
        isActive: true,
        status: UserStatus.ACTIVE,
      });

      console.log('✅ Admin user created successfully');
      return firebaseUser.uid;
    } catch (error) {
      console.error('❌ Error creating admin:', error);
      throw error;
    }
  }

  /**
   * Get all users (admin only)
   */
  static async getAllUsers(): Promise<User[]> {
    try {
      const usersSnapshot = await getDocs(collection(db, AdminService.USERS_COLLECTION));
      const users: User[] = [];
      
      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id,
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          role: data.role || UserRole.RECOMMENDER,
          createdAt: data.createdAt?.toDate() || new Date(),
          isActive: data.isActive !== false,
          status: data.status || UserStatus.ACTIVE,
        });
      });
      
      return users;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Check if a user is the first admin
   */
  static async isUserFirstAdmin(userId: string): Promise<boolean> {
    try {
      const firstAdminDoc = await getDoc(
        doc(db, AdminService.ADMIN_CONFIG_COLLECTION, AdminService.FIRST_ADMIN_DOC)
      );
      
      if (firstAdminDoc.exists()) {
        const data = firstAdminDoc.data();
        return data.adminId === userId;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking if user is first admin:', error);
      return false;
    }
  }

  /**
   * Update user status
   */
  static async updateUserStatus(userId: string, status: UserStatus): Promise<void> {
    try {
      await updateDoc(doc(db, AdminService.USERS_COLLECTION, userId), {
        status: status,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  /**
   * Remove an admin user (only first admin can do this)
   */
  static async removeAdmin(adminId: string): Promise<void> {
    try {
      console.log('🔍 Starting admin removal process...');
      
      // Get the current authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Vous devez être connecté pour supprimer un admin');
      }
      
      // Check if the current user is the first admin
      const isCurrentUserFirstAdmin = await AdminService.isUserFirstAdmin(currentUser.uid);
      if (!isCurrentUserFirstAdmin) {
        throw new Error('Seul le premier administrateur peut supprimer des admins');
      }

      // Prevent removing the first admin
      if (await AdminService.isUserFirstAdmin(adminId)) {
        throw new Error('Impossible de supprimer le premier administrateur');
      }

      console.log('✅ Current user is first admin, proceeding with admin removal...');

      // Remove the Firestore user document
      await setDoc(doc(db, AdminService.USERS_COLLECTION, adminId), {
        deletedAt: new Date(),
        isDeleted: true,
      });

      console.log('✅ Admin user removed successfully');
    } catch (error) {
      console.error('❌ Error removing admin:', error);
      throw error;
    }
  }

  /**
   * Remove admin privileges from a user
   */
  static async removeAdminPrivileges(userId: string): Promise<void> {
    try {
      // Check if this is the first admin
      const firstAdminDoc = await getDoc(
        doc(db, AdminService.ADMIN_CONFIG_COLLECTION, AdminService.FIRST_ADMIN_DOC)
      );
      
      if (firstAdminDoc.exists()) {
        const firstAdminData = firstAdminDoc.data();
        if (firstAdminData.adminId === userId) {
          throw new Error('Cannot remove privileges from the first admin');
        }
      }

      // Downgrade to recommender role
      await updateDoc(doc(db, AdminService.USERS_COLLECTION, userId), {
        role: UserRole.RECOMMENDER,
        status: UserStatus.ACTIVE,
      });
    } catch (error) {
      console.error('Error removing admin privileges:', error);
      throw error;
    }
  }

  /**
   * Get system statistics for dashboard
   */
  static async getSystemStats(): Promise<{
    totalUsers: number;
    totalAdmins: number;
    totalAgencies: number;
    totalRecommenders: number;
    activeUsers: number;
  }> {
    try {
      const usersSnapshot = await getDocs(collection(db, AdminService.USERS_COLLECTION));
      
      let totalUsers = 0;
      let totalAdmins = 0;
      let totalAgencies = 0;
      let totalRecommenders = 0;
      let activeUsers = 0;

      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        totalUsers++;
        
        if (data.isActive) {
          activeUsers++;
        }

        switch (data.role) {
          case UserRole.ADMIN:
            totalAdmins++;
            break;
          case UserRole.AGENCY:
            totalAgencies++;
            break;
          case UserRole.RECOMMENDER:
            totalRecommenders++;
            break;
        }
      });

      return {
        totalUsers,
        totalAdmins,
        totalAgencies,
        totalRecommenders,
        activeUsers,
      };
    } catch (error) {
      console.error('Error getting system stats:', error);
      throw error;
    }
  }

  /**
   * Get admin activity log
   */
  static async getAdminActivityLog(limitCount: number = 50): Promise<any[]> {
    try {
      // This would typically query an activity log collection
      // For now, return empty array
      return [];
    } catch (error) {
      console.error('Error getting admin activity log:', error);
      throw error;
    }
  }

  /**
   * Update admin profile
   */
  static async updateAdminProfile(adminId: string, updates: Partial<User>): Promise<void> {
    try {
      const updateData: any = {};
      
      if (updates.firstName) updateData.firstName = updates.firstName;
      if (updates.lastName) updateData.lastName = updates.lastName;
      if (updates.phone) updateData.phone = updates.phone;
      
      await updateDoc(doc(db, AdminService.USERS_COLLECTION, adminId), updateData);
    } catch (error) {
      console.error('Error updating admin profile:', error);
      throw error;
    }
  }
}
